/**
 * SystemSettingsPage - Platform-wide configuration for super-admins
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
    Settings, 
    Mail, 
    Save, 
    Send, 
    CheckCircle, 
    AlertCircle,
    Loader2,
    Shield,
    Server,
    RefreshCw,
    XCircle,
    HelpCircle,
    Wifi,
    WifiOff,
    Upload,
    Globe,
    Clock,
    Hash,
    FolderOpen,
    HardDrive,
    Lock,
    Link2,
    FileText,
    Database,
    Key,
    Users,
    Activity,
    Palette,
    Image,
    Eye,
    LayoutTemplate,
    Plus,
    Trash2,
    GripVertical,
    RotateCcw,
    Package,
    ShieldCheck,
    Inbox,
    KeyRound,
    Plug,
    Coins,
    Layers
} from 'lucide-react';
import { EmailTemplatesPanel } from './EmailTemplatesPage';
import VinylOrderModesPanel from '../../components/admin/VinylOrderModesPanel';
import {
    OverviewTab as StorageOverviewTab,
    LifecycleTab as StorageLifecycleTab,
    AnalyticsTab as StorageAnalyticsTab,
    CostTab as StorageCostTab,
    FilesTab as StorageFilesTab,
} from './StorageManagementPage';
import { EmailLogPanel } from '../EmailLogPage';
import LoginPageImagesPanel from '../../components/LoginPageImagesPanel';
// PartnerProgrammesPanel moved to /admin/partners → Affinity Programmes
// (2026-02). Import removed intentionally.
// LandingPagesPanel moved to Marketing hub at /admin/campaigns
// (2026-02). The panel component itself still lives in
// /components/LandingPagesPanel.jsx and is imported by CampaignsHubPage.
import FlowAiTokensPanel from '../../components/FlowAiTokensPanel';
import ExchangeRatesPage from './ExchangeRatesPage';
import CountriesManager from '../../components/CountriesManager';
import EconomicWebhookSettingsCard from '../../components/EconomicWebhookSettingsCard';
import { ReorderPolicyAdminPanel } from '../../components/ReorderPolicyAdminPanel';
import NcbSettingsCard from '../../components/NcbSettingsCard';

import { API_BASE } from '../../lib/apiBase';
const API = API_BASE;

// Status badge component
const StatusBadge = ({ status, label }) => {
    const statusConfig = {
        valid: { icon: CheckCircle, className: 'bg-green-100 text-green-700 border-green-200' },
        connected: { icon: CheckCircle, className: 'bg-green-100 text-green-700 border-green-200' },
        missing: { icon: XCircle, className: 'bg-amber-100 text-amber-700 border-amber-200' },
        failed: { icon: XCircle, className: 'bg-red-100 text-red-700 border-red-200' },
        invalid: { icon: XCircle, className: 'bg-red-100 text-red-700 border-red-200' },
        not_configured: { icon: AlertCircle, className: 'bg-slate-100 text-slate-600 border-slate-200' },
        unknown: { icon: HelpCircle, className: 'bg-slate-100 text-slate-500 border-slate-200' },
        error: { icon: AlertCircle, className: 'bg-red-100 text-red-700 border-red-200' },
    };
    
    const config = statusConfig[status] || statusConfig.unknown;
    const Icon = config.icon;
    
    return (
        <Badge variant="outline" className={`${config.className} font-medium`}>
            <Icon className="h-3 w-3 mr-1" />
            {label || status}
        </Badge>
    );
};

// Two-level navigation grouping for System Settings. Each tab `value` maps to
// an existing <TabsContent>; nav-only entries (navTo) route to standalone pages.
const SETTINGS_SECTIONS = [
    { id: 'general', label: 'General', icon: Globe, tabs: [
        { value: 'general', label: 'General', icon: Globe },
        { value: 'upload', label: 'Upload Defaults', icon: Upload },
        { value: 'reorder', label: 'Reorder', icon: Package, testid: 'tab-reorder' },
        { value: 'order_modes', label: 'Order Modes', icon: Layers, testid: 'tab-order-modes' },
    ] },
    { id: 'email_comms', label: 'Email & Communications', icon: Mail, tabs: [
        { value: 'email', label: 'Email', icon: Mail },
        { value: 'landing', label: 'New Customer Welcome', icon: LayoutTemplate, testid: 'tab-new-customer-welcome' },
    ] },
    { id: 'reference', label: 'Reference Data', icon: Coins, tabs: [
        { value: 'exchange_rates', label: 'Exchange Rates', icon: Coins, testid: 'tab-exchange-rates' },
        { value: 'countries', label: 'Countries', icon: Globe, testid: 'tab-countries' },
    ] },
    { id: 'integrations', label: 'Integrations', icon: Plug, tabs: [
        { value: 'integrations', label: 'Integrations', icon: Plug, testid: 'tab-integrations', navTo: '/admin/integrations' },
        { value: 'ncb', label: 'NCB', icon: FileText, testid: 'tab-ncb' },
        { value: 'flow_ai', label: 'Flow AI Access', icon: KeyRound, testid: 'tab-flow-ai' },
    ] },
    { id: 'security', label: 'Security & Access', icon: Lock, tabs: [
        { value: 'security', label: 'Security', icon: Lock },
        { value: 'domains', label: 'Domains', icon: Link2 },
        { value: 'audit', label: 'Audit Log', icon: Activity },
    ] },
    { id: 'platform', label: 'Platform & Appearance', icon: Image, tabs: [
        { value: 'login_page', label: 'Login Page', icon: Image, testid: 'tab-login-page' },
        { value: 'storage', label: 'Storage', icon: HardDrive },
    ] },
];

const sectionIdForTab = (tab) =>
    SETTINGS_SECTIONS.find((s) => s.tabs.some((t) => t.value === tab))?.id || SETTINGS_SECTIONS[0].id;


export default function SystemSettingsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const location = window.location;
    const urlParams = new URLSearchParams(location.search);
    const initialTab = urlParams.get('tab') || 'email';
    const initialSubTab = urlParams.get('sub') || 'sending';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [openSection, setOpenSection] = useState(sectionIdForTab(initialTab));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingUpload, setSavingUpload] = useState(false);
    const [savingGeneral, setSavingGeneral] = useState(false);
    const [testing, setTesting] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [settings, setSettings] = useState(null);
    const [emailStatus, setEmailStatus] = useState(null);
    const [testRecipient, setTestRecipient] = useState('');
    const [emailForm, setEmailForm] = useState({
        sender_name: '',
        sender_email: '',
        reply_to: ''
    });
    const [uploadForm, setUploadForm] = useState({
        default_expiry_days: 7,
        max_files_default: '',
        default_categories: ['master', 'artwork']
    });
    const [generalForm, setGeneralForm] = useState({
        system_name: 'Owntune',
        timezone: 'Europe/Copenhagen',
        default_currency: 'EUR',
        show_new_customer_landing: true
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [hasUploadChanges, setHasUploadChanges] = useState(false);
    const [hasGeneralChanges, setHasGeneralChanges] = useState(false);
    const [hasStorageChanges, setHasStorageChanges] = useState(false);
    const [hasSecurityChanges, setHasSecurityChanges] = useState(false);
    const [hasDomainChanges, setHasDomainChanges] = useState(false);
    const [emailSubTab, setEmailSubTab] = useState(initialSubTab);
    const [savingStorage, setSavingStorage] = useState(false);
    const [savingSecurity, setSavingSecurity] = useState(false);
    const [savingDomains, setSavingDomains] = useState(false);
    const [auditLog, setAuditLog] = useState([]);
    const [loadingAudit, setLoadingAudit] = useState(false);
    
    const [storageForm, setStorageForm] = useState({
        max_file_size_mb: 500,
        allowed_extensions: ['wav', 'mp3', 'aiff', 'flac', 'pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff', 'psd', 'ai', 'eps'],
        chunk_size_mb: 5
    });
    const [securityForm, setSecurityForm] = useState({
        session_timeout_minutes: 480,
        max_login_attempts: 5,
        lockout_duration_minutes: 30,
        require_strong_passwords: true
    });
    const [domainForm, setDomainForm] = useState({
        primary_domain: '',
        allowed_origins: []
    });
    const [newOrigin, setNewOrigin] = useState('');
    const [brandingForm, setBrandingForm] = useState({
        logo_url: '',
        primary_color: '#2563eb',
        footer_text: '',
        company_name: 'Owntune',
        support_email: ''
    });
    const [hasBrandingChanges, setHasBrandingChanges] = useState(false);
    const [savingBranding, setSavingBranding] = useState(false);
    const [emailPreview, setEmailPreview] = useState('');
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    
    // Landing page CMS state
    const [landingForm, setLandingForm] = useState({
        headline: 'Know your vinyl production cost — before you commit.',
        subtext: 'Adjust format and quantity — your total updates instantly.',
        button_text: 'Get Instant Quote',
        tagline: 'No commitment. No hidden costs.',
        preview_card: {
            product_name: '12" Black Vinyl',
            product_specs: '300 units • Standard Sleeve',
            per_unit_price: '€6.95',
            total_price: '€2,085'
        },
        trust_points: ['Instant pricing', 'No hidden fees', 'Professional production'],
        hero_image_url: null
    });
    const [hasLandingChanges, setHasLandingChanges] = useState(false);
    const [savingLanding, setSavingLanding] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [newTrustPoint, setNewTrustPoint] = useState('');

    // Available file categories
    const FILE_CATEGORIES = [
        { value: 'master', label: 'Master' },
        { value: 'audio', label: 'Audio' },
        { value: 'audio_side_a', label: 'Audio Side A' },
        { value: 'audio_side_b', label: 'Audio Side B' },
        { value: 'artwork', label: 'Artwork' },
        { value: 'tracklist', label: 'Tracklist' },
        { value: 'other', label: 'Other' }
    ];

    // Available timezones (common ones)
    const TIMEZONES = [
        'Europe/Copenhagen',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'America/New_York',
        'America/Los_Angeles',
        'Asia/Tokyo',
        'Australia/Sydney',
        'UTC'
    ];

    // Available currencies
    const CURRENCIES = ['EUR', 'USD', 'GBP', 'DKK', 'SEK', 'NOK'];

    // Check super-admin access
    const isSuperAdmin = user?.role === 'super_admin';
    // Admins now have full System Settings access too — keep the existing
    // `isSuperAdmin` flag for any super-admin-only UI tells (e.g. badge),
    // but use `canAccessSystemSettings` for the actual gate.
    const canAccessSystemSettings = user?.role === 'super_admin' || user?.role === 'admin';

    // Fetch email status
    const fetchEmailStatus = useCallback(async () => {
        try {
            setCheckingStatus(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/admin/system-settings/email/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmailStatus(response.data);
        } catch (error) {
            console.error('Failed to fetch email status:', error);
        } finally {
            setCheckingStatus(false);
        }
    }, []);

    // Fetch settings
    useEffect(() => {
        if (!canAccessSystemSettings) {
            setLoading(false);
            return;
        }
        
        const fetchSettings = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API}/api/admin/system-settings`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSettings(response.data);
                setEmailForm({
                    sender_name: response.data.email?.sender_name || '',
                    sender_email: response.data.email?.sender_email || '',
                    reply_to: response.data.email?.reply_to || ''
                });
                setUploadForm({
                    default_expiry_days: response.data.upload_links?.default_expiry_days || 7,
                    max_files_default: response.data.upload_links?.max_files_default || '',
                    default_categories: response.data.upload_links?.default_categories || ['master', 'artwork']
                });
                setGeneralForm({
                    system_name: response.data.general?.system_name || 'Owntune',
                    timezone: response.data.general?.timezone || 'Europe/Copenhagen',
                    default_currency: response.data.general?.default_currency || 'EUR',
                    show_new_customer_landing: response.data.general?.show_new_customer_landing ?? true
                });
                setStorageForm({
                    max_file_size_mb: response.data.storage?.max_file_size_mb || 500,
                    allowed_extensions: response.data.storage?.allowed_extensions || ['wav', 'mp3', 'aiff', 'flac', 'pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff', 'psd', 'ai', 'eps'],
                    chunk_size_mb: response.data.storage?.chunk_size_mb || 5
                });
                setSecurityForm({
                    session_timeout_minutes: response.data.security?.session_timeout_minutes || 480,
                    max_login_attempts: response.data.security?.max_login_attempts || 5,
                    lockout_duration_minutes: response.data.security?.lockout_duration_minutes || 30,
                    require_strong_passwords: response.data.security?.require_strong_passwords ?? true
                });
                setDomainForm({
                    primary_domain: response.data.domains?.primary_domain || '',
                    allowed_origins: response.data.domains?.allowed_origins || []
                });
                setBrandingForm({
                    logo_url: response.data.email_branding?.logo_url || '',
                    primary_color: response.data.email_branding?.primary_color || '#2563eb',
                    footer_text: response.data.email_branding?.footer_text || '',
                    company_name: response.data.email_branding?.company_name || 'Owntune',
                    support_email: response.data.email_branding?.support_email || ''
                });
                
                // Fetch landing page content
                fetchLandingPage();
                
                // Also fetch email status
                fetchEmailStatus();
            } catch (error) {
                if (error.response?.status === 403) {
                    toast.error('Super-admin access required');
                    navigate('/admin');
                } else {
                    toast.error('Failed to load settings');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [navigate, canAccessSystemSettings, fetchEmailStatus]);

    // Track changes
    useEffect(() => {
        if (!settings) return;
        
        const original = settings.email || {};
        const changed = 
            emailForm.sender_name !== (original.sender_name || '') ||
            emailForm.sender_email !== (original.sender_email || '') ||
            emailForm.reply_to !== (original.reply_to || '');
        
        setHasChanges(changed);
    }, [emailForm, settings]);

    // Track upload changes
    useEffect(() => {
        if (!settings) return;
        
        const original = settings.upload_links || {};
        const originalCategories = original.default_categories || ['master', 'artwork'];
        const categoriesChanged = JSON.stringify(uploadForm.default_categories.sort()) !== 
                                   JSON.stringify(originalCategories.sort());
        
        const changed = 
            uploadForm.default_expiry_days !== (original.default_expiry_days || 7) ||
            (uploadForm.max_files_default || null) !== (original.max_files_default || null) ||
            categoriesChanged;
        
        setHasUploadChanges(changed);
    }, [uploadForm, settings]);

    // Track general changes
    useEffect(() => {
        if (!settings) return;
        
        const original = settings.general || {};
        const changed = 
            generalForm.system_name !== (original.system_name || 'Owntune') ||
            generalForm.timezone !== (original.timezone || 'Europe/Copenhagen') ||
            generalForm.default_currency !== (original.default_currency || 'EUR') ||
            generalForm.show_new_customer_landing !== (original.show_new_customer_landing ?? true);
        
        setHasGeneralChanges(changed);
    }, [generalForm, settings]);

    const handleSaveEmail = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/api/admin/system-settings/email`, emailForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                email: response.data.email
            }));
            setHasChanges(false);
            toast.success('Email settings saved successfully');
            
            // Refresh status after saving
            fetchEmailStatus();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveUpload = async () => {
        setSavingUpload(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                default_expiry_days: uploadForm.default_expiry_days,
                max_files_default: uploadForm.max_files_default || null,
                default_categories: uploadForm.default_categories
            };
            const response = await axios.put(`${API}/api/admin/system-settings/upload-defaults`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                upload_links: response.data.upload_links
            }));
            setHasUploadChanges(false);
            toast.success('Upload defaults saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save upload defaults');
        } finally {
            setSavingUpload(false);
        }
    };

    const handleSaveGeneral = async () => {
        setSavingGeneral(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/api/admin/system-settings/general`, generalForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                general: response.data.general
            }));
            setHasGeneralChanges(false);
            toast.success('General settings saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save general settings');
        } finally {
            setSavingGeneral(false);
        }
    };

    const toggleCategory = (category) => {
        setUploadForm(prev => {
            const categories = prev.default_categories.includes(category)
                ? prev.default_categories.filter(c => c !== category)
                : [...prev.default_categories, category];
            return { ...prev, default_categories: categories };
        });
    };

    const handleSaveStorage = async () => {
        setSavingStorage(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/api/admin/system-settings/storage`, storageForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                storage: response.data.storage
            }));
            setHasStorageChanges(false);
            toast.success('Storage settings saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save storage settings');
        } finally {
            setSavingStorage(false);
        }
    };

    const handleSaveSecurity = async () => {
        setSavingSecurity(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/api/admin/system-settings/security`, securityForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                security: response.data.security
            }));
            setHasSecurityChanges(false);
            toast.success('Security settings saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save security settings');
        } finally {
            setSavingSecurity(false);
        }
    };

    const handleSaveDomains = async () => {
        setSavingDomains(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/api/admin/system-settings/domains`, domainForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                domains: response.data.domains
            }));
            setHasDomainChanges(false);
            toast.success('Domain settings saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save domain settings');
        } finally {
            setSavingDomains(false);
        }
    };

    const addOrigin = () => {
        if (newOrigin.trim() && !domainForm.allowed_origins.includes(newOrigin.trim())) {
            setDomainForm(prev => ({
                ...prev,
                allowed_origins: [...prev.allowed_origins, newOrigin.trim()]
            }));
            setNewOrigin('');
        }
    };

    const removeOrigin = (origin) => {
        setDomainForm(prev => ({
            ...prev,
            allowed_origins: prev.allowed_origins.filter(o => o !== origin)
        }));
    };

    const toggleExtension = (ext) => {
        setStorageForm(prev => ({
            ...prev,
            allowed_extensions: prev.allowed_extensions.includes(ext)
                ? prev.allowed_extensions.filter(e => e !== ext)
                : [...prev.allowed_extensions, ext]
        }));
    };

    const fetchAuditLog = async () => {
        setLoadingAudit(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/admin/system-settings/audit-log?limit=50`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAuditLog(response.data.entries || []);
        } catch (error) {
            console.error('Failed to fetch audit log:', error);
        } finally {
            setLoadingAudit(false);
        }
    };

    const handleSettingsTabChange = (value) => {
        setActiveTab(value);
        setOpenSection(sectionIdForTab(value));
        if (value === 'audit') fetchAuditLog();
    };

    const handleSettingsSubTabClick = (tab) => {
        if (tab.navTo) {
            navigate(tab.navTo);
            return;
        }
        handleSettingsTabChange(tab.value);
    };

    const handleSettingsSectionClick = (section) => {
        setOpenSection(section.id);
        if (!section.tabs.some((t) => t.value === activeTab)) {
            const firstContent = section.tabs.find((t) => !t.navTo) || section.tabs[0];
            handleSettingsSubTabClick(firstContent);
        }
    };


    // Track storage changes
    useEffect(() => {
        if (!settings) return;
        const original = settings.storage || {};
        const changed = 
            storageForm.max_file_size_mb !== (original.max_file_size_mb || 500) ||
            storageForm.chunk_size_mb !== (original.chunk_size_mb || 5) ||
            JSON.stringify(storageForm.allowed_extensions.sort()) !== JSON.stringify((original.allowed_extensions || []).sort());
        setHasStorageChanges(changed);
    }, [storageForm, settings]);

    // Track security changes
    useEffect(() => {
        if (!settings) return;
        const original = settings.security || {};
        const changed = 
            securityForm.session_timeout_minutes !== (original.session_timeout_minutes || 480) ||
            securityForm.max_login_attempts !== (original.max_login_attempts || 5) ||
            securityForm.lockout_duration_minutes !== (original.lockout_duration_minutes || 30) ||
            securityForm.require_strong_passwords !== (original.require_strong_passwords ?? true);
        setHasSecurityChanges(changed);
    }, [securityForm, settings]);

    // Track domain changes
    useEffect(() => {
        if (!settings) return;
        const original = settings.domains || {};
        const changed = 
            domainForm.primary_domain !== (original.primary_domain || '') ||
            JSON.stringify(domainForm.allowed_origins.sort()) !== JSON.stringify((original.allowed_origins || []).sort());
        setHasDomainChanges(changed);
    }, [domainForm, settings]);

    // Track branding changes
    useEffect(() => {
        if (!settings) return;
        const original = settings.email_branding || {};
        const changed = 
            brandingForm.logo_url !== (original.logo_url || '') ||
            brandingForm.primary_color !== (original.primary_color || '#2563eb') ||
            brandingForm.footer_text !== (original.footer_text || '') ||
            brandingForm.company_name !== (original.company_name || 'Owntune') ||
            brandingForm.support_email !== (original.support_email || '');
        setHasBrandingChanges(changed);
    }, [brandingForm, settings]);

    const handleSaveBranding = async () => {
        setSavingBranding(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/api/admin/system-settings/email-branding`, brandingForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(prev => ({
                ...prev,
                email_branding: response.data.email_branding
            }));
            setHasBrandingChanges(false);
            toast.success('Email branding saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save email branding');
        } finally {
            setSavingBranding(false);
        }
    };

    const fetchEmailPreview = async () => {
        setLoadingPreview(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/admin/system-settings/email-branding/preview`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmailPreview(response.data.preview_html);
        } catch (error) {
            toast.error('Failed to load email preview');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        const allowedTypes = ['image/png', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PNG or SVG file');
            return;
        }
        
        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Logo file must be under 2MB');
            return;
        }
        
        setUploadingLogo(true);
        try {
            const token = localStorage.getItem('token');
            
            // Upload file through backend (base64 encoded)
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const base64Data = event.target.result.split(',')[1]; // Remove data:image/png;base64, prefix
                    
                    const response = await axios.post(
                        `${API}/api/admin/system-settings/email-branding/upload-logo-direct`,
                        {
                            filename: file.name,
                            content_type: file.type,
                            file_data: base64Data
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    const { logo_url } = response.data;
                    
                    // Update form with new logo URL
                    setBrandingForm(prev => ({ ...prev, logo_url }));
                    
                    // Auto-save the branding settings
                    await axios.put(`${API}/api/admin/system-settings/email-branding`, 
                        { ...brandingForm, logo_url },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    setSettings(prev => ({
                        ...prev,
                        email_branding: { ...prev?.email_branding, logo_url }
                    }));
                    
                    toast.success('Logo uploaded successfully');
                } catch (error) {
                    console.error('Logo upload error:', error);
                    toast.error(error.response?.data?.detail || error.message || 'Failed to upload logo');
                } finally {
                    setUploadingLogo(false);
                    e.target.value = '';
                }
            };
            
            reader.onerror = () => {
                toast.error('Failed to read file');
                setUploadingLogo(false);
            };
            
            reader.readAsDataURL(file);
            
        } catch (error) {
            console.error('Logo upload error:', error);
            toast.error(error.response?.data?.detail || error.message || 'Failed to upload logo');
            setUploadingLogo(false);
            e.target.value = '';
        }
    };

    const handleRemoveLogo = async () => {
        if (!window.confirm('Remove the current logo?')) return;
        
        setRemovingLogo(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API}/api/admin/system-settings/email-branding/logo`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setBrandingForm(prev => ({ ...prev, logo_url: '' }));
            setSettings(prev => ({
                ...prev,
                email_branding: response.data.email_branding
            }));
            
            toast.success('Logo removed');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove logo');
        } finally {
            setRemovingLogo(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testRecipient.trim()) {
            toast.error('Please enter a recipient email address');
            return;
        }
        
        setTesting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/api/admin/system-settings/email/test`, 
                { recipient_email: testRecipient },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send test email');
        } finally {
            setTesting(false);
        }
    };

    // ==================== Landing Page Functions ====================
    
    const fetchLandingPage = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/admin/system-settings/landing-page`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLandingForm({
                headline: response.data.headline || 'Know your vinyl production cost — before you commit.',
                subtext: response.data.subtext || 'Adjust format and quantity — your total updates instantly.',
                button_text: response.data.button_text || 'Get Instant Quote',
                tagline: response.data.tagline || 'No commitment. No hidden costs.',
                preview_card: response.data.preview_card || {
                    product_name: '12" Black Vinyl',
                    product_specs: '300 units • Standard Sleeve',
                    per_unit_price: '€6.95',
                    total_price: '€2,085'
                },
                trust_points: response.data.trust_points || ['Instant pricing', 'No hidden fees', 'Professional production'],
                hero_image_url: response.data.hero_image_url || null
            });
        } catch (error) {
            console.error('Failed to load landing page content:', error);
        }
    };

    const handleSaveLanding = async () => {
        setSavingLanding(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/admin/system-settings/landing-page`, landingForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHasLandingChanges(false);
            toast.success('Landing page content saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save landing page content');
        } finally {
            setSavingLanding(false);
        }
    };

    const handleResetLanding = async () => {
        if (!confirm('Reset landing page to default content? This cannot be undone.')) return;
        
        setSavingLanding(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/api/admin/system-settings/landing-page/reset`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLandingForm({
                headline: response.data.landing_page.headline,
                subtext: response.data.landing_page.subtext,
                button_text: response.data.landing_page.button_text,
                tagline: response.data.landing_page.tagline,
                preview_card: response.data.landing_page.preview_card,
                trust_points: response.data.landing_page.trust_points,
                hero_image_url: response.data.landing_page.hero_image_url
            });
            setHasLandingChanges(false);
            toast.success('Landing page reset to defaults');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset landing page');
        } finally {
            setSavingLanding(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a JPG, PNG, WebP, or GIF image');
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }
        
        setUploadingImage(true);
        try {
            const token = localStorage.getItem('token');
            
            // Get presigned upload URL
            const urlResponse = await axios.post(
                `${API}/api/admin/system-settings/landing-page/upload-image`,
                null,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { filename: file.name, content_type: file.type }
                }
            );
            
            const { upload_url, image_url } = urlResponse.data;
            
            // Upload directly to S3
            await axios.put(upload_url, file, {
                headers: { 'Content-Type': file.type }
            });
            
            // Update form with the new image URL
            setLandingForm(prev => ({ ...prev, hero_image_url: image_url }));
            setHasLandingChanges(true);
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    const addTrustPoint = () => {
        if (newTrustPoint.trim() && !landingForm.trust_points.includes(newTrustPoint.trim())) {
            setLandingForm(prev => ({
                ...prev,
                trust_points: [...prev.trust_points, newTrustPoint.trim()]
            }));
            setNewTrustPoint('');
            setHasLandingChanges(true);
        }
    };

    const removeTrustPoint = (index) => {
        setLandingForm(prev => ({
            ...prev,
            trust_points: prev.trust_points.filter((_, i) => i !== index)
        }));
        setHasLandingChanges(true);
    };

    const moveTrustPoint = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= landingForm.trust_points.length) return;
        
        const newPoints = [...landingForm.trust_points];
        [newPoints[index], newPoints[newIndex]] = [newPoints[newIndex], newPoints[index]];
        
        setLandingForm(prev => ({ ...prev, trust_points: newPoints }));
        setHasLandingChanges(true);
    };

    // Track landing page changes
    useEffect(() => {
        // Simple flag - set to true when any field changes
        // Will be reset on save
    }, [landingForm]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!canAccessSystemSettings) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <Shield className="h-16 w-16 text-amber-500" />
                <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
                <p className="text-slate-500">Admin access is required for system settings.</p>
                <Button variant="outline" onClick={() => navigate('/admin')}>
                    Return to Dashboard
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="system-settings-page">
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                            <Settings className="h-6 w-6 text-slate-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold text-slate-900">System Settings</h1>
                            <p className="text-sm text-slate-500">Platform-wide configuration</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full">
                        <Shield className="h-4 w-4" />
                        Super-Admin Only
                    </div>
                </div>

                {/* Tabs — two-level navigation (sections → tabs) */}
                <Tabs value={activeTab} onValueChange={handleSettingsTabChange} className="space-y-4">
                {/* Level 1: section switcher */}
                <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" data-testid="settings-sections">
                    {SETTINGS_SECTIONS.map((section) => {
                        const SectionIcon = section.icon;
                        const isOpen = openSection === section.id;
                        return (
                            <button
                                key={section.id}
                                type="button"
                                onClick={() => handleSettingsSectionClick(section)}
                                data-testid={`settings-section-${section.id}`}
                                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${isOpen ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                <SectionIcon className="h-4 w-4" />
                                {section.label}
                            </button>
                        );
                    })}
                </div>

                {/* Level 2: tabs within the open section */}
                <TabsList className="bg-slate-100 flex-wrap h-auto">
                    {(SETTINGS_SECTIONS.find((s) => s.id === openSection)?.tabs || []).map((tab) => {
                        const TabIcon = tab.icon;
                        if (tab.navTo) {
                            return (
                                <button
                                    key={tab.value}
                                    type="button"
                                    onClick={() => navigate(tab.navTo)}
                                    data-testid={tab.testid}
                                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                                >
                                    <TabIcon className="h-4 w-4" />
                                    {tab.label}
                                </button>
                            );
                        }
                        return (
                            <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2" data-testid={tab.testid}>
                                <TabIcon className="h-4 w-4" />
                                {tab.label}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {/* Email Settings Tab — Unified with sub-tabs */}
                <TabsContent value="email" className="space-y-4">
                    {/* Sub-tab navigation */}
                    <div className="flex items-center gap-1 border-b pb-0">
                        {[
                            { key: 'sending', label: 'Sending', icon: Server },
                            { key: 'deliverability', label: 'Deliverability', icon: ShieldCheck },
                            { key: 'branding', label: 'Branding', icon: Palette },
                            { key: 'templates', label: 'Templates', icon: FileText },
                            { key: 'logs', label: 'Logs', icon: Inbox },
                        ].map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                data-testid={`email-subtab-${key}`}
                                onClick={() => setEmailSubTab(key)}
                                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                                    emailSubTab === key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Sub-tab: Sending */}
                    {emailSubTab === 'sending' && (
                    <div className="space-y-6">
                    {/* SMTP Provider Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Server className="h-5 w-5 text-slate-500" />
                                    Active SMTP Provider
                                    {emailStatus?.config_source && (
                                        <Badge variant="outline" className="ml-2 text-xs font-normal">
                                            from {emailStatus.config_source === 'integrations' ? 'Integrations' : 'Environment'}
                                        </Badge>
                                    )}
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={fetchEmailStatus}
                                    disabled={checkingStatus}
                                >
                                    <RefreshCw className={`h-4 w-4 mr-1 ${checkingStatus ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {emailStatus ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Host</p>
                                        <p className="font-medium text-slate-900">
                                            {emailStatus.smtp_host || 'Not configured'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Port</p>
                                        <p className="font-medium text-slate-900">
                                            {emailStatus.smtp_port || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Username</p>
                                        <p className="font-medium text-slate-900 truncate">
                                            {emailStatus.smtp_user || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Status</p>
                                        <div className="flex items-center gap-2">
                                            {emailStatus.connection_status === 'connected' ? (
                                                <Wifi className="h-4 w-4 text-green-600" />
                                            ) : (
                                                <WifiOff className="h-4 w-4 text-red-500" />
                                            )}
                                            <StatusBadge 
                                                status={emailStatus.connection_status} 
                                                label={emailStatus.connection_status === 'connected' ? 'Connected' : 
                                                       emailStatus.connection_status === 'not_configured' ? 'Not Configured' : 'Failed'}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading SMTP status...
                                </div>
                            )}
                            {emailStatus?.connection_error && (
                                <Alert className="mt-3 bg-red-50 border-red-200">
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                    <AlertDescription className="text-red-700">
                                        {emailStatus.connection_error}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>


                    {/* Email Sender Settings Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mail className="h-5 w-5 text-violet-500" />
                                Email Sender Settings
                            </CardTitle>
                            <CardDescription>
                                Configure how emails are sent from the platform. These settings override environment defaults.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Info Alert */}
                            <Alert className="bg-blue-50 border-blue-200">
                                <AlertCircle className="h-4 w-4 text-blue-600" />
                                <AlertDescription className="text-blue-800">
                                    SMTP connection settings (host, port, credentials) are configured in{' '}
                                    <a href="/admin/integrations" className="underline font-medium">Integrations</a>.
                                    These settings control branding and sender identity.
                                </AlertDescription>
                            </Alert>

                            {/* Form Fields */}
                            <div className="grid gap-6 max-w-xl">
                                <div className="space-y-2">
                                    <Label htmlFor="sender_name">Sender Name</Label>
                                    <Input
                                        id="sender_name"
                                        placeholder="Owntune"
                                        value={emailForm.sender_name}
                                        onChange={(e) => setEmailForm(prev => ({ 
                                            ...prev, 
                                            sender_name: e.target.value 
                                        }))}
                                        data-testid="email-sender-name"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Display name shown in recipients' email client (e.g., "Owntune" or "Owntune Notifications")
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="sender_email">Sender Email</Label>
                                    <Input
                                        id="sender_email"
                                        type="email"
                                        placeholder="uploads@owntune.com"
                                        value={emailForm.sender_email}
                                        onChange={(e) => setEmailForm(prev => ({ 
                                            ...prev, 
                                            sender_email: e.target.value 
                                        }))}
                                        data-testid="email-sender-email"
                                    />
                                    <p className="text-xs text-slate-500">
                                        The "From" address for outgoing emails. Leave empty to use SMTP default.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="reply_to">Reply-To Email</Label>
                                    <Input
                                        id="reply_to"
                                        type="email"
                                        placeholder="support@owntune.com"
                                        value={emailForm.reply_to}
                                        onChange={(e) => setEmailForm(prev => ({ 
                                            ...prev, 
                                            reply_to: e.target.value 
                                        }))}
                                        data-testid="email-reply-to"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Where replies should be directed. Useful if sender is a no-reply address.
                                    </p>
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t">
                                <Button
                                    onClick={handleSaveEmail}
                                    disabled={saving || !hasChanges}
                                    data-testid="save-email-btn"
                                >
                                    {saving ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>

                            {/* Last Updated */}
                            {settings?.updated_at && (
                                <p className="text-xs text-slate-400 text-right">
                                    Last updated: {new Date(settings.updated_at).toLocaleString()}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Test Email Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Send className="h-5 w-5 text-blue-500" />
                                Test Email Delivery
                            </CardTitle>
                            <CardDescription>
                                Send a test email to verify deliverability with different providers (Gmail, Outlook, etc.)
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-end gap-3 max-w-xl">
                                <div className="flex-1 space-y-2">
                                    <Label htmlFor="test_recipient">Test Recipient Email</Label>
                                    <Input
                                        id="test_recipient"
                                        type="email"
                                        placeholder="test@example.com"
                                        value={testRecipient}
                                        onChange={(e) => setTestRecipient(e.target.value)}
                                        data-testid="test-recipient-input"
                                    />
                                </div>
                                <Button
                                    onClick={handleTestEmail}
                                    disabled={testing || !testRecipient.trim()}
                                    data-testid="send-test-email-btn"
                                >
                                    {testing ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4 mr-2" />
                                    )}
                                    Send Test Email
                                </Button>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                                A test email will be sent using the current sender settings. Check the recipient's inbox and spam folder.
                            </p>
                        </CardContent>
                    </Card>
                    </div>
                    )}

                    {/* Sub-tab: Deliverability */}
                    {emailSubTab === 'deliverability' && (
                    <div className="space-y-6">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                    Email Deliverability
                                </CardTitle>
                                <CardDescription>
                                    DNS records for {emailStatus?.sender_domain || 'your sender domain'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {emailStatus ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* SPF */}
                                            <div className="p-4 bg-slate-50 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium text-slate-700">SPF Record</span>
                                                    <StatusBadge 
                                                        status={emailStatus.spf_status} 
                                                        label={emailStatus.spf_status === 'valid' ? 'Valid' : 
                                                               emailStatus.spf_status === 'missing' ? 'Missing' : 'Error'}
                                                    />
                                                </div>
                                                {emailStatus.spf_record && (
                                                    <p className="text-xs text-slate-500 font-mono truncate" title={emailStatus.spf_record}>
                                                        {emailStatus.spf_record}
                                                    </p>
                                                )}
                                            </div>

                                            {/* DKIM */}
                                            <div className="p-4 bg-slate-50 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium text-slate-700">DKIM Signature</span>
                                                    <StatusBadge 
                                                        status={emailStatus.dkim_status} 
                                                        label={emailStatus.dkim_status === 'valid' ? 'Valid' : 
                                                               emailStatus.dkim_status === 'unknown' ? 'Unknown' : 'Missing'}
                                                    />
                                                </div>
                                                {emailStatus.dkim_note && (
                                                    <p className="text-xs text-slate-500">
                                                        {emailStatus.dkim_note}
                                                    </p>
                                                )}
                                            </div>

                                            {/* DMARC */}
                                            <div className="p-4 bg-slate-50 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium text-slate-700">DMARC Policy</span>
                                                    <StatusBadge 
                                                        status={emailStatus.dmarc_status} 
                                                        label={emailStatus.dmarc_status === 'valid' ? 'Valid' : 
                                                               emailStatus.dmarc_status === 'missing' ? 'Missing' : 'Error'}
                                                    />
                                                </div>
                                                {emailStatus.dmarc_record && (
                                                    <p className="text-xs text-slate-500 font-mono truncate" title={emailStatus.dmarc_record}>
                                                        {emailStatus.dmarc_record}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {!emailStatus.sender_domain && (
                                            <Alert className="bg-amber-50 border-amber-200">
                                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                                <AlertDescription className="text-amber-700">
                                                    Configure a sender email address in the Sending tab to check deliverability records.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-slate-500">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Checking deliverability...
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Setup Guidance */}
                        <Card className="bg-slate-50 border-dashed">
                            <CardContent className="py-4 space-y-3">
                                <p className="text-sm font-medium text-slate-700">Setup Guide</p>
                                <div className="space-y-2 text-xs text-slate-500">
                                    <p><strong>SPF</strong> — Add a TXT record to your sending domain's DNS: <code className="bg-white px-1 rounded text-slate-600">v=spf1 include:your-smtp-provider.com ~all</code></p>
                                    <p><strong>DKIM</strong> — Typically managed by your SMTP provider. Check their documentation for DKIM key setup.</p>
                                    <p><strong>DMARC</strong> — Add a TXT record at <code className="bg-white px-1 rounded text-slate-600">_dmarc.yourdomain.com</code>: <code className="bg-white px-1 rounded text-slate-600">v=DMARC1; p=quarantine;</code></p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    )}

                    {/* Sub-tab: Branding */}
                    {emailSubTab === 'branding' && (
                    <div className="space-y-6">
                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Branding Settings Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Palette className="h-5 w-5 text-pink-500" />
                                    Email Branding
                                </CardTitle>
                                <CardDescription>
                                    Customize the appearance of outgoing system emails.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid gap-4">
                                    {/* Company Name */}
                                    <div className="space-y-2">
                                        <Label htmlFor="company_name">Company Name</Label>
                                        <Input
                                            id="company_name"
                                            type="text"
                                            placeholder="Owntune"
                                            value={brandingForm.company_name}
                                            onChange={(e) => setBrandingForm(prev => ({ 
                                                ...prev, 
                                                company_name: e.target.value
                                            }))}
                                            data-testid="branding-company"
                                        />
                                    </div>

                                    {/* Logo Upload */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Image className="h-4 w-4" />
                                            Logo
                                        </Label>
                                        
                                        {brandingForm.logo_url ? (
                                            <div className="space-y-3">
                                                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                                                    <div className="flex items-center justify-between">
                                                        <img 
                                                            src={brandingForm.logo_url} 
                                                            alt="Logo preview" 
                                                            className="max-h-12 max-w-[200px] object-contain"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                        <div className="flex items-center gap-2">
                                                            <label className="cursor-pointer">
                                                                <input type="file" accept="image/png,image/svg+xml" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} />
                                                                <Button type="button" variant="outline" size="sm" className="h-8" disabled={uploadingLogo} asChild>
                                                                    <span>{uploadingLogo ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}Replace</span>
                                                                </Button>
                                                            </label>
                                                            <Button type="button" variant="ghost" size="sm" className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleRemoveLogo} disabled={removingLogo}>
                                                                {removingLogo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer">
                                                <input type="file" accept="image/png,image/svg+xml" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} data-testid="branding-logo-input" />
                                                <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                                                    {uploadingLogo ? <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" /> : <Upload className="h-8 w-8 text-slate-400 mb-2" />}
                                                    <p className="text-sm font-medium text-slate-600">{uploadingLogo ? 'Uploading...' : 'Click to upload logo'}</p>
                                                    <p className="text-xs text-slate-400 mt-1">PNG or SVG, max 2MB</p>
                                                </div>
                                            </label>
                                        )}
                                        <p className="text-xs text-slate-500">Recommended size: 200x50 pixels. PNG or SVG format.</p>
                                    </div>

                                    {/* Primary Color */}
                                    <div className="space-y-2">
                                        <Label htmlFor="primary_color">Brand Color</Label>
                                        <div className="flex gap-2">
                                            <input type="color" id="primary_color" value={brandingForm.primary_color} onChange={(e) => setBrandingForm(prev => ({ ...prev, primary_color: e.target.value }))} className="h-10 w-14 rounded border border-slate-200 cursor-pointer" />
                                            <Input type="text" value={brandingForm.primary_color} onChange={(e) => setBrandingForm(prev => ({ ...prev, primary_color: e.target.value }))} className="flex-1" placeholder="#2563eb" data-testid="branding-color" />
                                        </div>
                                    </div>

                                    {/* Support Email */}
                                    <div className="space-y-2">
                                        <Label htmlFor="support_email">Support Email</Label>
                                        <Input id="support_email" type="email" placeholder="support@example.com" value={brandingForm.support_email} onChange={(e) => setBrandingForm(prev => ({ ...prev, support_email: e.target.value }))} data-testid="branding-support" />
                                        <p className="text-xs text-slate-500">Shown in email footer for support inquiries</p>
                                    </div>

                                    {/* Footer Text */}
                                    <div className="space-y-2">
                                        <Label htmlFor="footer_text">Custom Footer Text</Label>
                                        <textarea id="footer_text" rows={2} placeholder="Your custom message in email footers..." value={brandingForm.footer_text} onChange={(e) => setBrandingForm(prev => ({ ...prev, footer_text: e.target.value }))} className="w-full px-3 py-2 text-sm border border-input rounded-md focus:ring-2 focus:ring-accent/20" data-testid="branding-footer" />
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={handleSaveBranding} disabled={savingBranding || !hasBrandingChanges} data-testid="save-branding-btn">
                                        {savingBranding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                        Save Changes
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Email Preview Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Eye className="h-5 w-5 text-slate-500" />
                                        Email Preview
                                    </div>
                                    <Button variant="outline" size="sm" onClick={fetchEmailPreview} disabled={loadingPreview}>
                                        {loadingPreview ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                                        Load Preview
                                    </Button>
                                </CardTitle>
                                <CardDescription>See how your branded emails will look</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loadingPreview ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                                    </div>
                                ) : emailPreview ? (
                                    <div className="border rounded-lg overflow-hidden bg-slate-100">
                                        <iframe srcDoc={emailPreview} title="Email Preview" className="w-full h-96 bg-white" sandbox="" />
                                    </div>
                                ) : (
                                    <div className="text-center py-16 text-slate-500">
                                        <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                                        <p>Click "Load Preview" to see your branded email template</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                    </div>
                    )}

                    {/* Sub-tab: Templates */}
                    {emailSubTab === 'templates' && (
                    <div className="space-y-4">
                        <EmailTemplatesPanel />
                    </div>
                    )}

                    {/* Sub-tab: Logs */}
                    {emailSubTab === 'logs' && (
                    <div className="space-y-4">
                        <EmailLogPanel />
                    </div>
                    )}

                </TabsContent>

                {/* Upload Defaults Tab */}
                <TabsContent value="upload" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Upload className="h-5 w-5 text-indigo-500" />
                                Upload Link Defaults
                            </CardTitle>
                            <CardDescription>
                                Configure default settings for new upload links. These can be overridden when creating individual links.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-6 max-w-xl">
                                {/* Default Expiry */}
                                <div className="space-y-2">
                                    <Label htmlFor="default_expiry" className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-slate-400" />
                                        Default Expiry (Days)
                                    </Label>
                                    <Input
                                        id="default_expiry"
                                        type="number"
                                        min="1"
                                        max="365"
                                        placeholder="7"
                                        value={uploadForm.default_expiry_days}
                                        onChange={(e) => setUploadForm(prev => ({ 
                                            ...prev, 
                                            default_expiry_days: parseInt(e.target.value) || 7
                                        }))}
                                        data-testid="upload-default-expiry"
                                    />
                                    <p className="text-xs text-slate-500">
                                        How many days until upload links expire by default (1-365)
                                    </p>
                                </div>

                                {/* Max Files */}
                                <div className="space-y-2">
                                    <Label htmlFor="max_files" className="flex items-center gap-2">
                                        <Hash className="h-4 w-4 text-slate-400" />
                                        Maximum Files (Optional)
                                    </Label>
                                    <Input
                                        id="max_files"
                                        type="number"
                                        min="1"
                                        placeholder="Unlimited"
                                        value={uploadForm.max_files_default}
                                        onChange={(e) => setUploadForm(prev => ({ 
                                            ...prev, 
                                            max_files_default: e.target.value ? parseInt(e.target.value) : ''
                                        }))}
                                        data-testid="upload-max-files"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Leave empty for unlimited files per upload link
                                    </p>
                                </div>

                                {/* Default Categories */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <FolderOpen className="h-4 w-4 text-slate-400" />
                                        Default Allowed Categories
                                    </Label>
                                    <div className="flex flex-wrap gap-2">
                                        {FILE_CATEGORIES.map((cat) => (
                                            <Button
                                                key={cat.value}
                                                type="button"
                                                variant={uploadForm.default_categories.includes(cat.value) ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => toggleCategory(cat.value)}
                                                data-testid={`category-${cat.value}`}
                                            >
                                                {cat.label}
                                            </Button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Select which file categories are allowed by default
                                    </p>
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t">
                                <Button
                                    onClick={handleSaveUpload}
                                    disabled={savingUpload || !hasUploadChanges}
                                    data-testid="save-upload-btn"
                                >
                                    {savingUpload ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* General Settings Tab */}
                <TabsContent value="general" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Globe className="h-5 w-5 text-emerald-500" />
                                General Settings
                            </CardTitle>
                            <CardDescription>
                                Configure general platform settings like system name, timezone, and default currency.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-6 max-w-xl">
                                {/* System Name */}
                                <div className="space-y-2">
                                    <Label htmlFor="system_name">System Name</Label>
                                    <Input
                                        id="system_name"
                                        placeholder="Owntune"
                                        value={generalForm.system_name}
                                        onChange={(e) => setGeneralForm(prev => ({ 
                                            ...prev, 
                                            system_name: e.target.value 
                                        }))}
                                        data-testid="general-system-name"
                                    />
                                    <p className="text-xs text-slate-500">
                                        The name displayed throughout the platform
                                    </p>
                                </div>

                                {/* Timezone */}
                                <div className="space-y-2">
                                    <Label htmlFor="timezone">Default Timezone</Label>
                                    <select
                                        id="timezone"
                                        value={generalForm.timezone}
                                        onChange={(e) => setGeneralForm(prev => ({ 
                                            ...prev, 
                                            timezone: e.target.value 
                                        }))}
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                        data-testid="general-timezone"
                                    >
                                        {TIMEZONES.map((tz) => (
                                            <option key={tz} value={tz}>{tz}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500">
                                        Used for date/time display and scheduling
                                    </p>
                                </div>

                                {/* Default Currency */}
                                <div className="space-y-2">
                                    <Label htmlFor="currency">Default Currency</Label>
                                    <select
                                        id="currency"
                                        value={generalForm.default_currency}
                                        onChange={(e) => setGeneralForm(prev => ({ 
                                            ...prev, 
                                            default_currency: e.target.value 
                                        }))}
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                        data-testid="general-currency"
                                    >
                                        {CURRENCIES.map((curr) => (
                                            <option key={curr} value={curr}>{curr}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500">
                                        Default currency for new orders and quotes
                                    </p>
                                </div>
                                
                                {/* New Customer Landing Page Toggle */}
                                <div className="space-y-3 pt-4 border-t">
                                    <Label className="text-base font-medium">Customer Experience</Label>
                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                        <div className="space-y-1">
                                            <Label htmlFor="show_landing" className="font-medium">
                                                New Customer Landing Page
                                            </Label>
                                            <p className="text-xs text-slate-500">
                                                Show a pricing-focused landing page for new customers with no orders
                                            </p>
                                        </div>
                                        <Switch
                                            id="show_landing"
                                            checked={generalForm.show_new_customer_landing}
                                            onCheckedChange={(checked) => setGeneralForm(prev => ({ 
                                                ...prev, 
                                                show_new_customer_landing: checked 
                                            }))}
                                            data-testid="show-new-customer-landing-toggle"
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        When disabled, new customers will see the standard orders page immediately.
                                    </p>
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t">
                                <Button
                                    onClick={handleSaveGeneral}
                                    disabled={savingGeneral || !hasGeneralChanges}
                                    data-testid="save-general-btn"
                                >
                                    {savingGeneral ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* e-conomic webhook receiver — near-real-time push
                        updates from e-conomic (booking, betaling, forfald).
                        Complements the daily 04:00 UTC sync. */}
                    <EconomicWebhookSettingsCard />
                </TabsContent>

                {/* Reorder Settings Tab — global system-wide minimum reorder
                    quantities per product family (Vinyl / CD / DVD / Blu-ray).
                    Per-catalogue-entry overrides live on the entry edit form. */}
                <TabsContent value="reorder" className="space-y-6">
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <Package className="h-4 w-4 text-emerald-500" />
                        <span>
                            Global system-wide defaults. Applies to every catalogue
                            entry across all companies unless the entry has its own
                            <span className="font-medium"> Minimum reorder quantity </span>
                            override.
                        </span>
                    </div>
                    <ReorderPolicyAdminPanel />
                </TabsContent>

                {/* Order Modes Tab — enable/disable the three vinyl order
                    modes (Quick / Guided / Expert) and pick a global
                    default. Company-level overrides live on the Company
                    edit page. */}
                <TabsContent value="order_modes" className="space-y-6">
                    <VinylOrderModesPanel />
                </TabsContent>

                {/* NCB Settings Tab — recipient email + optional CC list
                    used by the monthly NCB report send-to-email action. */}
                <TabsContent value="ncb" className="space-y-6">
                    <NcbSettingsCard />
                </TabsContent>

                {/* Storage Settings Tab */}
                <TabsContent value="storage" className="space-y-6">
                    <Tabs defaultValue="settings">
                        <TabsList>
                            <TabsTrigger value="settings" data-testid="storage-subtab-settings">Upload Settings</TabsTrigger>
                            <TabsTrigger value="overview" data-testid="storage-subtab-overview">Overview</TabsTrigger>
                            <TabsTrigger value="files" data-testid="storage-subtab-files">All Files</TabsTrigger>
                            <TabsTrigger value="lifecycle" data-testid="storage-subtab-lifecycle">Lifecycle</TabsTrigger>
                            <TabsTrigger value="analytics" data-testid="storage-subtab-analytics">Analytics</TabsTrigger>
                            <TabsTrigger value="cost" data-testid="storage-subtab-cost">Cost</TabsTrigger>
                        </TabsList>
                        <TabsContent value="settings" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <HardDrive className="h-5 w-5 text-blue-500" />
                                Storage Configuration
                            </CardTitle>
                            <CardDescription>
                                Configure file storage limits and allowed file types for uploads.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-6 max-w-xl">
                                {/* Max File Size */}
                                <div className="space-y-2">
                                    <Label htmlFor="max_file_size">Maximum File Size (MB)</Label>
                                    <Input
                                        id="max_file_size"
                                        type="number"
                                        min="1"
                                        max="5000"
                                        value={storageForm.max_file_size_mb}
                                        onChange={(e) => setStorageForm(prev => ({ 
                                            ...prev, 
                                            max_file_size_mb: parseInt(e.target.value) || 500
                                        }))}
                                        data-testid="storage-max-size"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Maximum size for individual file uploads (1-5000 MB)
                                    </p>
                                </div>

                                {/* Chunk Size */}
                                <div className="space-y-2">
                                    <Label htmlFor="chunk_size">Upload Chunk Size (MB)</Label>
                                    <Input
                                        id="chunk_size"
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={storageForm.chunk_size_mb}
                                        onChange={(e) => setStorageForm(prev => ({ 
                                            ...prev, 
                                            chunk_size_mb: parseInt(e.target.value) || 5
                                        }))}
                                        data-testid="storage-chunk-size"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Size of each chunk for resumable uploads (1-100 MB)
                                    </p>
                                </div>

                                {/* Allowed Extensions */}
                                <div className="space-y-2">
                                    <Label>Allowed File Extensions</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {['wav', 'mp3', 'aiff', 'flac', 'pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff', 'psd', 'ai', 'eps', 'zip'].map((ext) => (
                                            <Button
                                                key={ext}
                                                type="button"
                                                variant={storageForm.allowed_extensions.includes(ext) ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => toggleExtension(ext)}
                                            >
                                                .{ext}
                                            </Button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Select which file types are allowed for upload
                                    </p>
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t">
                                <Button
                                    onClick={handleSaveStorage}
                                    disabled={savingStorage || !hasStorageChanges}
                                    data-testid="save-storage-btn"
                                >
                                    {savingStorage ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                        </TabsContent>
                        <TabsContent value="overview" className="mt-4">
                            <StorageOverviewTab />
                        </TabsContent>
                        <TabsContent value="files" className="mt-4">
                            <StorageFilesTab />
                        </TabsContent>
                        <TabsContent value="lifecycle" className="mt-4">
                            <StorageLifecycleTab />
                        </TabsContent>
                        <TabsContent value="analytics" className="mt-4">
                            <StorageAnalyticsTab />
                        </TabsContent>
                        <TabsContent value="cost" className="mt-4">
                            <StorageCostTab />
                        </TabsContent>
                    </Tabs>
                </TabsContent>

                {/* Security Settings Tab */}
                <TabsContent value="security" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Lock className="h-5 w-5 text-red-500" />
                                Security Settings
                            </CardTitle>
                            <CardDescription>
                                Configure authentication and session security settings.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-6 max-w-xl">
                                {/* Session Timeout */}
                                <div className="space-y-2">
                                    <Label htmlFor="session_timeout">Session Timeout (Minutes)</Label>
                                    <Input
                                        id="session_timeout"
                                        type="number"
                                        min="15"
                                        max="1440"
                                        value={securityForm.session_timeout_minutes}
                                        onChange={(e) => setSecurityForm(prev => ({ 
                                            ...prev, 
                                            session_timeout_minutes: parseInt(e.target.value) || 480
                                        }))}
                                        data-testid="security-session-timeout"
                                    />
                                    <p className="text-xs text-slate-500">
                                        How long users stay logged in without activity (15-1440 minutes)
                                    </p>
                                </div>

                                {/* Max Login Attempts */}
                                <div className="space-y-2">
                                    <Label htmlFor="max_attempts">Max Login Attempts</Label>
                                    <Input
                                        id="max_attempts"
                                        type="number"
                                        min="3"
                                        max="10"
                                        value={securityForm.max_login_attempts}
                                        onChange={(e) => setSecurityForm(prev => ({ 
                                            ...prev, 
                                            max_login_attempts: parseInt(e.target.value) || 5
                                        }))}
                                        data-testid="security-max-attempts"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Number of failed login attempts before lockout (3-10)
                                    </p>
                                </div>

                                {/* Lockout Duration */}
                                <div className="space-y-2">
                                    <Label htmlFor="lockout_duration">Lockout Duration (Minutes)</Label>
                                    <Input
                                        id="lockout_duration"
                                        type="number"
                                        min="5"
                                        max="120"
                                        value={securityForm.lockout_duration_minutes}
                                        onChange={(e) => setSecurityForm(prev => ({ 
                                            ...prev, 
                                            lockout_duration_minutes: parseInt(e.target.value) || 30
                                        }))}
                                        data-testid="security-lockout"
                                    />
                                    <p className="text-xs text-slate-500">
                                        How long accounts are locked after max failed attempts (5-120 minutes)
                                    </p>
                                </div>

                                {/* Strong Passwords */}
                                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                    <div>
                                        <p className="font-medium text-slate-900">Require Strong Passwords</p>
                                        <p className="text-xs text-slate-500">
                                            Enforce minimum password complexity requirements
                                        </p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={securityForm.require_strong_passwords}
                                        onChange={(e) => setSecurityForm(prev => ({ 
                                            ...prev, 
                                            require_strong_passwords: e.target.checked
                                        }))}
                                        className="h-5 w-5 rounded border-slate-300"
                                        data-testid="security-strong-passwords"
                                    />
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t">
                                <Button
                                    onClick={handleSaveSecurity}
                                    disabled={savingSecurity || !hasSecurityChanges}
                                    data-testid="save-security-btn"
                                >
                                    {savingSecurity ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Domains Settings Tab */}
                <TabsContent value="domains" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Link2 className="h-5 w-5 text-purple-500" />
                                Domain Configuration
                            </CardTitle>
                            <CardDescription>
                                Configure allowed domains and CORS settings.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-6 max-w-xl">
                                {/* Primary Domain */}
                                <div className="space-y-2">
                                    <Label htmlFor="primary_domain">Primary Domain</Label>
                                    <Input
                                        id="primary_domain"
                                        type="text"
                                        placeholder="e.g., app.owntune.com"
                                        value={domainForm.primary_domain}
                                        onChange={(e) => setDomainForm(prev => ({ 
                                            ...prev, 
                                            primary_domain: e.target.value
                                        }))}
                                        data-testid="domain-primary"
                                    />
                                    <p className="text-xs text-slate-500">
                                        The main domain for your application
                                    </p>
                                </div>

                                {/* Allowed Origins */}
                                <div className="space-y-2">
                                    <Label>Allowed Origins (CORS)</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="text"
                                            placeholder="https://example.com"
                                            value={newOrigin}
                                            onChange={(e) => setNewOrigin(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOrigin())}
                                        />
                                        <Button type="button" variant="outline" onClick={addOrigin}>
                                            Add
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {domainForm.allowed_origins.map((origin) => (
                                            <Badge key={origin} variant="secondary" className="flex items-center gap-1">
                                                {origin}
                                                <button
                                                    type="button"
                                                    onClick={() => removeOrigin(origin)}
                                                    className="ml-1 hover:text-red-500"
                                                >
                                                    <XCircle className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Domains allowed to make cross-origin requests
                                    </p>
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t">
                                <Button
                                    onClick={handleSaveDomains}
                                    disabled={savingDomains || !hasDomainChanges}
                                    data-testid="save-domains-btn"
                                >
                                    {savingDomains ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>


                {/* Landing Page CMS Tab */}
                <TabsContent value="landing" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <LayoutTemplate className="h-5 w-5 text-indigo-500" />
                                New Customer Landing Page
                            </CardTitle>
                            <CardDescription>
                                Customize the landing page shown to new customers when they first log in (before they have any orders).
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-8">
                            {/* Main Content Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Main Content</h3>
                                
                                <div className="grid gap-4 max-w-2xl">
                                    {/* Headline */}
                                    <div className="space-y-2">
                                        <Label htmlFor="landing-headline">Headline</Label>
                                        <Input
                                            id="landing-headline"
                                            value={landingForm.headline}
                                            onChange={(e) => {
                                                setLandingForm(prev => ({ ...prev, headline: e.target.value }));
                                                setHasLandingChanges(true);
                                            }}
                                            placeholder="Enter headline..."
                                            data-testid="landing-headline"
                                        />
                                    </div>
                                    
                                    {/* Subtext */}
                                    <div className="space-y-2">
                                        <Label htmlFor="landing-subtext">Subtext</Label>
                                        <Input
                                            id="landing-subtext"
                                            value={landingForm.subtext}
                                            onChange={(e) => {
                                                setLandingForm(prev => ({ ...prev, subtext: e.target.value }));
                                                setHasLandingChanges(true);
                                            }}
                                            placeholder="Enter subtext..."
                                            data-testid="landing-subtext"
                                        />
                                    </div>
                                    
                                    {/* Button Text */}
                                    <div className="space-y-2">
                                        <Label htmlFor="landing-button">Button Text</Label>
                                        <Input
                                            id="landing-button"
                                            value={landingForm.button_text}
                                            onChange={(e) => {
                                                setLandingForm(prev => ({ ...prev, button_text: e.target.value }));
                                                setHasLandingChanges(true);
                                            }}
                                            placeholder="Enter button text..."
                                            data-testid="landing-button"
                                        />
                                    </div>
                                    
                                    {/* Tagline */}
                                    <div className="space-y-2">
                                        <Label htmlFor="landing-tagline">Tagline (below button)</Label>
                                        <Input
                                            id="landing-tagline"
                                            value={landingForm.tagline}
                                            onChange={(e) => {
                                                setLandingForm(prev => ({ ...prev, tagline: e.target.value }));
                                                setHasLandingChanges(true);
                                            }}
                                            placeholder="Enter tagline..."
                                            data-testid="landing-tagline"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Preview Card Section */}
                            <div className="space-y-4 pt-4 border-t">
                                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Preview Card Content</h3>
                                
                                <div className="grid gap-4 max-w-2xl">
                                    {/* Product Name */}
                                    <div className="space-y-2">
                                        <Label htmlFor="preview-product">Product Name</Label>
                                        <Input
                                            id="preview-product"
                                            value={landingForm.preview_card.product_name}
                                            onChange={(e) => {
                                                setLandingForm(prev => ({
                                                    ...prev,
                                                    preview_card: { ...prev.preview_card, product_name: e.target.value }
                                                }));
                                                setHasLandingChanges(true);
                                            }}
                                            placeholder='e.g., 12" Black Vinyl'
                                            data-testid="preview-product-name"
                                        />
                                    </div>
                                    
                                    {/* Product Specs */}
                                    <div className="space-y-2">
                                        <Label htmlFor="preview-specs">Product Specs</Label>
                                        <Input
                                            id="preview-specs"
                                            value={landingForm.preview_card.product_specs}
                                            onChange={(e) => {
                                                setLandingForm(prev => ({
                                                    ...prev,
                                                    preview_card: { ...prev.preview_card, product_specs: e.target.value }
                                                }));
                                                setHasLandingChanges(true);
                                            }}
                                            placeholder="e.g., 300 units • Standard Sleeve"
                                            data-testid="preview-specs"
                                        />
                                    </div>
                                    
                                    {/* Prices */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="preview-unit-price">Per Unit Price</Label>
                                            <Input
                                                id="preview-unit-price"
                                                value={landingForm.preview_card.per_unit_price}
                                                onChange={(e) => {
                                                    setLandingForm(prev => ({
                                                        ...prev,
                                                        preview_card: { ...prev.preview_card, per_unit_price: e.target.value }
                                                    }));
                                                    setHasLandingChanges(true);
                                                }}
                                                placeholder="e.g., €6.95"
                                                data-testid="preview-unit-price"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="preview-total-price">Total Price</Label>
                                            <Input
                                                id="preview-total-price"
                                                value={landingForm.preview_card.total_price}
                                                onChange={(e) => {
                                                    setLandingForm(prev => ({
                                                        ...prev,
                                                        preview_card: { ...prev.preview_card, total_price: e.target.value }
                                                    }));
                                                    setHasLandingChanges(true);
                                                }}
                                                placeholder="e.g., €2,085"
                                                data-testid="preview-total-price"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Trust Points Section */}
                            <div className="space-y-4 pt-4 border-t">
                                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Trust Points</h3>
                                <p className="text-sm text-slate-500">The checkmark items shown on the preview card</p>
                                
                                <div className="space-y-2 max-w-lg">
                                    {landingForm.trust_points.map((point, index) => (
                                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                                            <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                            <span className="flex-1 text-sm text-slate-700">{point}</span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => moveTrustPoint(index, 'up')}
                                                    disabled={index === 0}
                                                    className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                                                    title="Move up"
                                                >
                                                    <GripVertical className="h-4 w-4 rotate-180" />
                                                </button>
                                                <button
                                                    onClick={() => moveTrustPoint(index, 'down')}
                                                    disabled={index === landingForm.trust_points.length - 1}
                                                    className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                                                    title="Move down"
                                                >
                                                    <GripVertical className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => removeTrustPoint(index)}
                                                    className="p-1 text-slate-400 hover:text-red-600"
                                                    title="Remove"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Add new trust point */}
                                    <div className="flex items-center gap-2 mt-3">
                                        <Input
                                            value={newTrustPoint}
                                            onChange={(e) => setNewTrustPoint(e.target.value)}
                                            placeholder="Add a trust point..."
                                            onKeyDown={(e) => e.key === 'Enter' && addTrustPoint()}
                                            className="flex-1"
                                            data-testid="new-trust-point"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={addTrustPoint}
                                            disabled={!newTrustPoint.trim()}
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Hero Image Section */}
                            <div className="space-y-4 pt-4 border-t">
                                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Hero Image (Optional)</h3>
                                <p className="text-sm text-slate-500">Upload a custom image to display on the landing page</p>
                                
                                <div className="flex items-start gap-6 max-w-2xl">
                                    {/* Current image preview */}
                                    <div className="w-32 h-32 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden">
                                        {landingForm.hero_image_url ? (
                                            <img 
                                                src={landingForm.hero_image_url} 
                                                alt="Hero preview" 
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="text-center text-slate-400">
                                                <Image className="h-8 w-8 mx-auto mb-1" />
                                                <span className="text-xs">No image</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Upload controls */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <label className="cursor-pointer">
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                                    onChange={handleImageUpload}
                                                    className="hidden"
                                                    disabled={uploadingImage}
                                                />
                                                <Button variant="outline" size="sm" asChild disabled={uploadingImage}>
                                                    <span>
                                                        {uploadingImage ? (
                                                            <>
                                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                                Uploading...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Upload className="h-4 w-4 mr-2" />
                                                                Upload Image
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            </label>
                                            {landingForm.hero_image_url && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setLandingForm(prev => ({ ...prev, hero_image_url: null }));
                                                        setHasLandingChanges(true);
                                                    }}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Supported: JPG, PNG, WebP, GIF. Max 5MB.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-6 border-t">
                                <Button
                                    variant="outline"
                                    onClick={handleResetLanding}
                                    disabled={savingLanding}
                                    className="text-slate-600"
                                >
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Reset to Defaults
                                </Button>
                                <Button
                                    onClick={handleSaveLanding}
                                    disabled={savingLanding || !hasLandingChanges}
                                    data-testid="save-landing-btn"
                                >
                                    {savingLanding ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Save Changes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Live Preview Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Eye className="h-5 w-5 text-slate-500" />
                                Live Preview
                            </CardTitle>
                            <CardDescription>
                                This is how the landing page will appear to new customers
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="bg-slate-50 rounded-xl p-8">
                                <div className="grid lg:grid-cols-5 gap-8 items-center max-w-4xl mx-auto">
                                    {/* Left Side - Content */}
                                    <div className="lg:col-span-3 space-y-4">
                                        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                            {landingForm.headline || 'Your headline here'}
                                        </h1>
                                        <p className="text-slate-600">
                                            {landingForm.subtext || 'Your subtext here'}
                                        </p>
                                        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                            {landingForm.button_text || 'Button Text'}
                                        </Button>
                                        <p className="text-sm text-slate-500">
                                            {landingForm.tagline || 'Your tagline here'}
                                        </p>
                                    </div>
                                    
                                    {/* Right Side - Preview Card */}
                                    <div className="lg:col-span-2">
                                        <div className="bg-white rounded-xl border shadow-lg p-5 max-w-xs">
                                            {/* Product Summary */}
                                            <div className="mb-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                                                        <Package className="h-4 w-4 text-white" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-slate-900 text-sm">{landingForm.preview_card.product_name}</p>
                                                        <p className="text-xs text-slate-500">{landingForm.preview_card.product_specs}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Price Display */}
                                            <div className="border-t border-b border-slate-100 py-3 mb-4">
                                                <div className="flex items-baseline justify-between">
                                                    <span className="text-xs text-slate-500">Per unit</span>
                                                    <span className="text-xl font-bold text-slate-900">{landingForm.preview_card.per_unit_price}</span>
                                                </div>
                                                <div className="flex items-baseline justify-between mt-1">
                                                    <span className="text-xs text-slate-500">Total estimate</span>
                                                    <span className="text-base font-semibold text-slate-700">{landingForm.preview_card.total_price}</span>
                                                </div>
                                            </div>
                                            
                                            {/* Trust Points */}
                                            <div className="space-y-1.5">
                                                {landingForm.trust_points.map((point, index) => (
                                                    <div key={index} className="flex items-center gap-2 text-xs text-slate-600">
                                                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                                                        <span>{point}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Login Page Tab */}
                <TabsContent value="login_page" className="space-y-6">
                    <LoginPageImagesPanel />
                </TabsContent>

                {/* Partner Programmes moved to /admin/partners →
                    Affinity Programmes tab (2026-02). Import kept above
                    only for reference; safe to remove after clean-up. */}

                {/* Landing Pages moved to Marketing hub at
                    /admin/campaigns?tab=landing_pages (2026-02). */}

                {/* Flow AI Access Tab */}
                <TabsContent value="flow_ai" className="space-y-6">
                    <FlowAiTokensPanel />
                </TabsContent>

                {/* Reference Data → Exchange Rates (embedded in settings layout) */}
                <TabsContent value="exchange_rates" className="space-y-6">
                    <ExchangeRatesPage embedded />
                </TabsContent>

                {/* Reference Data → Countries (single master) */}
                <TabsContent value="countries" className="space-y-6">
                    <CountriesManager />
                </TabsContent>


                {/* Audit Log Tab */}
                <TabsContent value="audit" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Activity className="h-5 w-5 text-orange-500" />
                                    Audit Log
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={fetchAuditLog}
                                    disabled={loadingAudit}
                                >
                                    <RefreshCw className={`h-4 w-4 mr-1 ${loadingAudit ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                            </CardTitle>
                            <CardDescription>
                                View recent system activity and configuration changes.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loadingAudit ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                                </div>
                            ) : auditLog.length === 0 ? (
                                <div className="text-center py-8 text-slate-500">
                                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                                    <p>No audit log entries found</p>
                                    <p className="text-xs mt-1">System events will appear here</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {auditLog.map((entry, index) => (
                                        <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                                            <div className="p-1.5 bg-white rounded">
                                                <Activity className="h-4 w-4 text-slate-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-slate-900 text-sm">{entry.action}</p>
                                                <p className="text-xs text-slate-500">
                                                    {entry.user_email} • {new Date(entry.timestamp).toLocaleString()}
                                                </p>
                                                {entry.details && Object.keys(entry.details).length > 0 && (
                                                    <p className="text-xs text-slate-400 mt-1 truncate">
                                                        {JSON.stringify(entry.details)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            </main>
        </div>
    );
}
